import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { times, toLower } from 'lodash';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CompanyItem } from 'context/MDContext/types';
import {
  FUND_INVESTMENTS_COMPANIES_TITLE,
  FUND_INVESTMENTS_COMPANIES_TITLE_SINGULAR,
  FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE,
  FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE_SINGULAR,
} from 'pages/FundInvestments/common/constants';
import { getObjectValue } from 'utilities';
import CompanyListProps from './CompanyList.props';

const StyledRootGrid = styled(Grid)({
  width: '100%',
});

const StyledListContainer = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(1),
  border: '1px solid #8A9097',
  height: 300,
  borderRadius: 5,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  '& .scrollbar-container': {
    paddingTop: 0,
  },
}));

const StyledItemContainer = styled('fieldset')(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  border: 'none',
  padding: 0,
  margin: 0,
}));

const StyledListTitle = styled(Typography)({
  color: '#444',
  fontWeight: 600,
});

const StyledItemTitle = styled('legend')(({ theme }) => ({
  zIndex: 1,
  fontSize: 12,
  color: theme.palette.grey[300],
  fontWeight: 600,
  textTransform: 'uppercase',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    padding: `${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(0.5)} !important`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#8A9097 !important',
  },
  '& .MuiInputBase-adornedEnd': {
    paddingRight: `${theme.spacing(1)}px !important`,
  },
}));

const StyledSearchIcon = styled(SearchIcon)({
  color: '#8A9097',
});

const CompanyList = ({
  items,
  companyList,
  isLoading,
  selectedCompanies,
  setSelectedCompanies,
  isDeletingMD,
}: CompanyListProps) => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = storeValue;
  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const [filteredItems, setFilteredItems] = useState<typeof items | undefined>();

  const companyItems = useMemo(() => {
    if (items) {
      return items;
    }
    if (companyList) {
      return companyList.map(({ id, company }) => ({ id, name: company?.name ?? '', subItems: [] }));
    }
    return [];
  }, [items, companyList]);

  const handleFilterChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const filter = e.target.value;

      const tmpCompanyItems = companyItems.map(item => {
        const tmpSubItems = item.subItems.filter(subItem => toLower(subItem.name).includes(toLower(filter)));
        return {
          ...item,
          subItems: tmpSubItems,
        };
      });
      const tmpFilteredItems = tmpCompanyItems.filter(
        item =>
          toLower(item.name).includes(toLower(filter))
          || item.subItems.some(subItem => toLower(subItem.name).includes(toLower(filter)))
      );

      setFilteredItems(tmpFilteredItems);
    },
    [companyItems]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, option: { id: number; name: string }) => {
      const { checked } = e.target;

      let tmpSelectedItems = {
        ...selectedCompanies,
      };

      if (checked) {
        tmpSelectedItems = {
          ...tmpSelectedItems,
          [option.id]: selectedCompanies[option.id] || checked,
        };
      } else {
        delete tmpSelectedItems[option.id];
      }

      setSelectedCompanies(tmpSelectedItems);
    },
    [selectedCompanies, setSelectedCompanies]
  );

  const renderCompaniesToDelete = useMemo(
    () =>
      (filteredItems || []).map(item => (
        <FormControl key={`checkbox-${item.id}`} component="fieldset">
          <FormGroup>
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  id={`checkbox-${item.id}`}
                  size="small"
                  name={item.name}
                  checked={selectedCompanies[item.id as number] || false}
                  onChange={e => handleChange(e, { id: item.id as number, name: item.name })}
                />
              }
              label={item.name}
            />
          </FormGroup>
        </FormControl>
      )),
    [filteredItems, selectedCompanies, handleChange]
  );

  const renderSubItemCheckboxes = useCallback(
    (subItems: CompanyItem[]) =>
      subItems.map(subItem => (
        <FormControlLabel
          key={subItem.id}
          control={
            <Checkbox
              id={`checkbox-${subItem.id}`}
              size="small"
              name={subItem.name}
              checked={selectedCompanies[subItem.id] || false}
              onChange={e =>
                handleChange(e, {
                  ...subItem,
                })
              }
            />
          }
          label={subItem.name}
        />
      )),
    [handleChange, selectedCompanies]
  );

  const renderCompaniesToAdd = useMemo(
    () =>
      (filteredItems || []).map(item => {
        if (item.subItems.length) {
          return (
            <StyledItemContainer key={item.name}>
              <StyledItemTitle>{item.name}</StyledItemTitle>
              <FormGroup>{renderSubItemCheckboxes(item.subItems)}</FormGroup>
            </StyledItemContainer>
          );
        }

        return null;
      }),
    [filteredItems, renderSubItemCheckboxes]
  );

  const companyListTitle = useMemo(
    () =>
      `Select one or more ${
        areFundInvestmentsEnabled ? FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE : FUND_INVESTMENTS_COMPANIES_TITLE
      }:`,
    [areFundInvestmentsEnabled]
  );

  const companySearchPlaceholder = useMemo(
    () =>
      `Find ${
        areFundInvestmentsEnabled
          ? `an ${FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE_SINGULAR}`
          : `a ${FUND_INVESTMENTS_COMPANIES_TITLE_SINGULAR}`
      }`,
    [areFundInvestmentsEnabled]
  );

  const renderItems = useCallback(
    () => (isDeletingMD ? renderCompaniesToDelete : renderCompaniesToAdd),
    [isDeletingMD, renderCompaniesToAdd, renderCompaniesToDelete]
  );

  const renderSkeleton = useCallback(() => {
    const rows = 4;
    return times(rows, key => <Skeleton key={key} variant="text" height={40} />);
  }, []);

  useEffect(() => {
    if (companyItems) {
      setFilteredItems(companyItems);
    }
  }, [companyItems]);

  return (
    <StyledRootGrid>
      <StyledListTitle variant="subtitle2" gutterBottom>
        {companyListTitle}
      </StyledListTitle>
      <StyledTextField
        fullWidth
        variant="outlined"
        placeholder={companySearchPlaceholder}
        onChange={handleFilterChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <StyledSearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <StyledListContainer>
        <PerfectScrollbar options={{ minScrollbarLength: 50 }}>
          <Grid container direction="column" wrap="nowrap">
            {Array.isArray(filteredItems) && !isLoading ? renderItems() : renderSkeleton()}
          </Grid>
        </PerfectScrollbar>
      </StyledListContainer>
    </StyledRootGrid>
  );
};

export default CompanyList;
